import { PlanogramRelaxConstraint } from "@CommonTypes/merchflow/pog/pog";
import { compact } from "es-toolkit";
import { useMemo } from "react";
import { Tags } from "src/elements";
import {
  getPlanogramItemDisplayName,
  PlanogramItemView,
} from "src/components/Planogram/common/planogramItemView";

interface Props {
  item: PlanogramItemView;
  relaxedItemsMapping?: Record<string, PlanogramRelaxConstraint>;
  showMagnitude: boolean;
  dosDisplayAsPercentage: boolean;
}

export const ItemDetails = ({
  item,
  relaxedItemsMapping,
  showMagnitude,
  dosDisplayAsPercentage,
}: Props) => {
  const [dos, mos, capacity] = useMemo(() => {
    if (item.kind !== "SHELF_ITEM" || !item.shelf) {
      return [0, 0, 0];
    }

    let dos = 0;
    let mos = 0;
    let capacity = 0;

    for (const bay of item.shelf.bay.planogram.bays) {
      for (const shelf of bay.shelves) {
        for (const _item of shelf.items) {
          if (_item.productCode === item.productCode) {
            mos += _item.getMOSPercentage();
            if (dosDisplayAsPercentage) {
              dos += _item.getDaysOfSupplyPercentage();
            } else {
              dos += _item.getDaysOfSupply();
            }
            capacity += _item.getCapacity();
          }
        }
      }
    }

    return [dos, Math.floor(mos), Math.floor(capacity)];
  }, [item, dosDisplayAsPercentage]);

  return (
    <Tags
      tags={[
        { label: "Item ID", value: getPlanogramItemDisplayName(item) },
        ...(item.kind === "SHELF_ITEM" && item.shelf ? [{ label: "MOS", value: `${mos}%` }] : []),
        ...(item.kind === "SHELF_ITEM" && item.shelf
          ? [
              {
                label: "DOS",
                value: dosDisplayAsPercentage ? `${dos.toFixed(2)}%` : `${dos.toFixed(2)} days`,
              },
            ]
          : []),
        { label: "Movement", value: item.quantity.toFixed(2) },
        { label: "Sales", value: item.salesAmount.toFixed(2) },
        ...(item.ranking ? [{ label: "Ranking", value: item.ranking }] : []),
        ...(item.profit !== null ? [{ label: "Profit", value: item.profit }] : []),
        ...(item.kind === "SHELF_ITEM" && item.shelf
          ? [{ label: "Capacity", value: capacity }]
          : []),
        ...(item.kind === "SHELF_ITEM" &&
        relaxedItemsMapping &&
        relaxedItemsMapping[item.productCode]
          ? compact([
              {
                label: "Relaxation Type",
                value: relaxedItemsMapping[item.productCode].relaxationType,
              },
              showMagnitude && {
                label: "Magnitude",
                value: relaxedItemsMapping[item.productCode].magnitude,
              },
            ])
          : []),
      ]}
    />
  );
};
